import React from 'react';
import { CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';
import ContactForm from '../components/contact/ContactForm';
import ContactInfo from '../components/contact/ContactInfo';
import { faqs } from '../mock/mockData';
import SEOHead from '../components/seo/SEOHead';
import Breadcrumbs from '../components/seo/Breadcrumbs';
import GoogleReviewsStrip from '../components/GoogleReviewsStrip';

const Contact = () => {
  return (
    <div className="min-h-screen">
      <SEOHead
        title="Contact Us | Request Project Quotation | Alden Technologies"
        description="Request a free project quotation for your mineral water bottling plant. Call +91-9351292512, WhatsApp, or submit an enquiry — our engineers respond within 24 hours."
        path="/contact"
      />
      <Breadcrumbs items={[{ label: 'Contact' }]} />
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1530037335614-e68828dcf258"
            alt="Contact Us"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/90 to-emerald-700/70"></div>
        </div>
        
        <div className="container mx-auto px-4 z-10 relative text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl md:text-2xl text-emerald-100 max-w-3xl mx-auto">
            Let&apos;s discuss your beverage plant project requirements
          </p>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-3 gap-12">
            <ContactInfo />
            <ContactForm />
          </div>
        </div>
      </section>

      {/* Google Reviews - Social Proof */}
      <GoogleReviewsStrip />

      {/* Google Maps Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-green-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Visit Our Manufacturing Facility
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              See our state-of-the-art plant manufacturing facility in Jaipur
            </p>
          </div>
          <div className="max-w-5xl mx-auto">
            <div className="bg-white rounded-lg shadow-xl overflow-hidden">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d7110.088873356579!2d75.75609287612383!3d26.997144056455816!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x396db3cf773c5dbd%3A0xe6a4b662837762a4!2sAlden%20Technologies%20%7C%20Mineral%20Water%20Plant%20Manufacturer!5e0!3m2!1sen!2sin!4v1781521627089!5m2!1sen!2sin" 
                width="100%" 
                height="450" 
                style={{border:0}} 
                allowFullScreen="" 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Alden Technologies Location"
              />
            </div>
            <div className="mt-6 text-center">
              <p className="text-gray-700 text-lg">
                <strong>Alden Technologies</strong> - Mineral Water Plant Manufacturer
              </p>
              <p className="text-gray-600 mt-2">
                Jaipur, Rajasthan, India
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked <span className="text-emerald-600">Questions</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Find answers to common questions about our beverage plant solutions
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq) => (
                <AccordionItem 
                  key={faq.question} 
                  value={`item-${faq.question}`}
                  className="border-2 border-gray-200 rounded-lg px-6 hover:border-emerald-500 transition-colors"
                >
                  <AccordionTrigger className="text-left text-lg font-semibold text-gray-900 hover:text-emerald-600">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-gray-700 leading-relaxed pt-2">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Why <span className="text-emerald-600">Contact Us?</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {[
              {
                id: 'expert-consultation',
                title: "Expert Consultation",
                description: "Get professional advice from experienced beverage plant engineers"
              },
              {
                id: 'custom-solutions',
                title: "Custom Solutions",
                description: "Tailored plant design based on your specific requirements and budget"
              },
              {
                id: 'quick-response',
                title: "Quick Response",
                description: "Receive detailed quotation within 24 hours of inquiry submission"
              }
            ].map((item) => (
              <Card key={item.id} className="border-2 border-gray-200 text-center">
                <CardContent className="p-8">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
